import { type ComponentPropsWithoutRef, forwardRef } from "react"
import { Badge } from "./badge"
import { User } from "./iconOutline"
import { type IconSize } from "./iconCore"

export const avatarSizes = ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"] as const

export type AvatarSize = (typeof avatarSizes)[number]

export type AvatarProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Indicates the size of this avatar. Arbitrary sizes are not allowed so that
   * avatars are always given one of a narrow set of sizes that fit within the design system.
   *
   * @default "sm"
   */
  size?: AvatarSize
}

const avatarSizeToIconSize: Record<AvatarSize, IconSize> = {
  xs: "xs",
  sm: "xs",
  md: "sm",
  lg: "lg",
  xl: "lg",
  "2xl": "xl",
  "3xl": "2xl",
}

/**
 * This component is meant to wrap an `img`, or when using Next.js, an `Image`.
 * it adds a bit of styling for sizing + border-radius.
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size = "sm", children, ...props }: AvatarProps, ref) => {
    return (
      <div
        className={`avatar ${className ?? ""}`}
        data-size={size}
        ref={ref}
        {...props}
      >
        {children || <User data-size={avatarSizeToIconSize[size]} />}
        <div className="avatar-inner-ring" />
      </div>
    )
  },
)

Avatar.displayName = "Avatar"

export const profilePhotoBorderVariants = ["default", "live"] as const

export type ProfilePhotoProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Indicates the type of border to display around the avatar.
   *
   * @default "default"
   */
  borderVariant?: (typeof profilePhotoBorderVariants)[number]
}

/**
 * A large, prominent Avatar. Can optionally display LIVE status.
 * This is a separate component from Avatar (though it contains an Avatar),
 * because it requires a specific size (2xl) for the Avatar.
 */
export const ProfilePhoto = forwardRef<HTMLDivElement, ProfilePhotoProps>(
  ({ className, borderVariant = "default", children, ...props }, ref) => {
    return (
      <div
        className="profile-photo-container"
        data-border-variant={borderVariant}
        ref={ref}
        {...props}
      >
        <Avatar size="2xl">{children}</Avatar>
        {borderVariant === "live" && (
          <Badge size="sm" className="avatar-live-badge" variant="live">
            LIVE
          </Badge>
        )}
      </div>
    )
  },
)

ProfilePhoto.displayName = "ProfilePhoto"
